import React from "react";
import { Navigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { loadRazorpay } from "@/lib/razorpay";
import { toast } from "sonner";
import { Home, Eye, MessageSquare, Star, Loader2, Phone, Crown, CheckCircle2 } from "lucide-react";

const PLAN_META = [
  { tier: "agent", name: "Agent", key: "agent" },
  { tier: "agent_pro", name: "Agent Pro", key: "agent_pro" },
  { tier: "agent_business", name: "Agent Business", key: "agent_business" },
];

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${color}`}><Icon className="w-5 h-5" /></div>
      <div className="text-2xl font-heading font-extrabold text-slate-900">{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  );
}

export default function AgentDashboard() {
  const { user, loading, setUser } = useAuth();
  const dash = useQuery({ queryKey: ["my-dashboard"], queryFn: async () => (await api.get("/my/dashboard")).data, enabled: !!user });
  const { data: pricing } = useQuery({ queryKey: ["pricing"], queryFn: async () => (await api.get("/settings")).data.pricing });

  if (loading) return <div className="flex justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;
  if (!user) return <Navigate to="/" replace />;

  const d = dash.data || {};
  const subscribe = async (tier) => {
    try {
      const { data: order } = await api.post("/payments/subscription/create-order", { tier });
      const done = async () => { const { data } = await api.post("/payments/subscription/verify", { order_id: order.order_id, payment_id: "demo", signature: "demo" }); setUser(data.user); toast.success("Subscription active!"); dash.refetch(); };
      if (order.demo) { await done(); return; }
      await loadRazorpay();
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: "INR", order_id: order.order_id,
        name: "Dotlands Realty", description: `Subscription: ${tier}`,
        handler: async (res) => { const { data } = await api.post("/payments/subscription/verify", { order_id: res.razorpay_order_id, payment_id: res.razorpay_payment_id, signature: res.razorpay_signature }); setUser(data.user); toast.success("Subscription active!"); dash.refetch(); },
        theme: { color: "#0E3B2E" },
      });
      rzp.open();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8" data-testid="agent-dashboard">
      <Seo title="My Dashboard | Dotlands Realty" description="Manage your listings, views, leads and subscription on Dotlands Realty." path="/agent" noindex />
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">My Dashboard</h1>
          <p className="text-sm text-slate-500">Welcome back, {user.name || "there"}</p>
        </div>
        <div className="flex items-center gap-2">
          {user.subscription ? <Badge className="bg-emerald-600"><Crown className="w-3 h-3 mr-1" />{user.subscription.replace("_", " ")}</Badge> : <Badge variant="secondary">Free plan</Badge>}
          <Button asChild className="bg-[#C8A24C] hover:bg-[#A8863A] text-[#0E3B2E] font-bold rounded-full"><Link to="/list-property" data-testid="dash-post-btn">Post Property</Link></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={Home} label="Active listings" value={d.active ?? 0} color="bg-emerald-50 text-emerald-600" />
        <Stat icon={Eye} label="Total views" value={d.total_views ?? 0} color="bg-blue-50 text-blue-600" />
        <Stat icon={Star} label="Featured" value={d.featured ?? 0} color="bg-amber-50 text-amber-600" />
        <Stat icon={MessageSquare} label="Leads" value={d.leads?.length ?? 0} color="bg-purple-50 text-purple-600" />
      </div>

      {/* Subscription plans */}
      <h2 className="font-heading font-bold text-lg text-slate-900 mb-3">Agent subscriptions</h2>
      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        {PLAN_META.map((p) => {
          const active = user.subscription === p.tier;
          return (
            <div key={p.tier} className={`rounded-2xl border p-6 ${active ? "border-emerald-500 bg-emerald-50/40" : "border-slate-200 bg-white"}`} data-testid={`sub-plan-${p.tier}`}>
              <div className="font-heading font-bold text-[#0E3B2E]">{p.name}</div>
              <div className="text-2xl font-extrabold text-slate-900 mt-2">₹{pricing?.[p.key] ?? "—"}<span className="text-sm font-medium text-slate-400">/month</span></div>
              <Button className="w-full mt-4 bg-[#0E3B2E] hover:bg-[#0A2A20] text-white" disabled={active} onClick={() => subscribe(p.tier)} data-testid={`subscribe-${p.tier}`}>
                {active ? <><CheckCircle2 className="w-4 h-4 mr-1" /> Current plan</> : "Subscribe"}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Leads */}
      <h2 className="font-heading font-bold text-lg text-slate-900 mb-3">Recent leads on your listings</h2>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {dash.isLoading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
          : (d.leads?.length ? (
            <div className="divide-y divide-slate-100">
              {d.leads.map((l) => (
                <div key={l.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`agent-lead-${l.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2"><span className="font-semibold text-slate-900">{l.name}</span><Badge variant="secondary">{l.type === "direct_message" ? "Direct" : "Enquiry"}</Badge></div>
                    <a href={`tel:${l.phone}`} className="text-sm text-emerald-600 flex items-center gap-1 mt-0.5"><Phone className="w-3.5 h-3.5" /> +91 {l.phone}</a>
                    <p className="text-sm text-slate-500 mt-1">On: {l.property_title}</p>
                  </div>
                  <p className="text-xs text-slate-400">{new Date(l.created_at).toLocaleString("en-IN")}</p>
                </div>
              ))}
            </div>
          ) : <div className="text-center py-16 text-slate-400">No leads yet. They'll appear when buyers enquire on your listings.</div>)}
      </div>
    </div>
  );
}
